import apiClient, { safeJson } from '@/api/client';
import {
    IFacilitySubscription,
    CreateSubscriptionBody,
    UpdateSubscriptionBody,
    SubscriptionListParams,
    SubscriptionListResponse,
    SubscriptionSummaryResponse,
    MemberSubscriptionListResponse,
    PauseSubscriptionBody,
    PauseSubscriptionResponse,
    ResumeSubscriptionBody,
    ResumeSubscriptionResponse,
    CancelPauseBody,
    UpdatePausePeriodBody,
    PausePeriodsResponse,
    PauseMutationResponse,
    RefundSubscriptionBody,
    FacilityTransferPreview,
    FacilityTransferPreviewParams,
    FacilityTransferBody,
    FacilityTransferResult,
    FacilityTransferListResponse,
} from './types';

/**
 * @method get
 * @brief 회원별 이용권 조회
 */
export const getMemberSubscriptions = async ({
    centerId,
    memberId,
}: {
    centerId: string;
    memberId: string;
}) => {
    const res = await apiClient.get(`v1/centers/${centerId}/members/${memberId}/subscriptions`);
    const data = await safeJson(res);
    return {
        status: res.status,
        data: data as MemberSubscriptionListResponse | null,
    };
};

/**
 * @method get
 * @brief 센터 전체 이용권 조회
 */
export const getCenterSubscriptions = async ({
    centerId,
    status,
    category,
    search,
    dateBase,
    dateFrom,
    dateTo,
    page,
    limit,
}: SubscriptionListParams) => {
    const searchParams: Record<string, string> = {};
    if (status && status !== 'all') searchParams.status = status;
    if (category && category !== 'all') searchParams.category = category;
    if (search) searchParams.search = search;
    if (dateBase) searchParams.dateBase = dateBase;
    if (dateFrom) searchParams.dateFrom = dateFrom;
    if (dateTo) searchParams.dateTo = dateTo;
    if (page !== undefined) searchParams.page = String(page);
    if (limit !== undefined) searchParams.limit = String(limit);

    const res = await apiClient.get(`v1/centers/${centerId}/subscriptions`, { searchParams });
    const data = await safeJson(res);
    return {
        status: res.status,
        data: data as SubscriptionListResponse | null,
    };
};

export const getCenterSubscriptionSummary = async ({
    centerId,
    status,
    category,
    search,
    dateBase,
    dateFrom,
    dateTo,
}: SubscriptionListParams) => {
    const searchParams: Record<string, string> = {};
    if (status && status !== 'all') searchParams.status = status;
    if (category && category !== 'all') searchParams.category = category;
    if (search) searchParams.search = search;
    if (dateBase) searchParams.dateBase = dateBase;
    if (dateFrom) searchParams.dateFrom = dateFrom;
    if (dateTo) searchParams.dateTo = dateTo;

    const res = await apiClient.get(`v1/centers/${centerId}/subscriptions/summary`, { searchParams });
    const data = await safeJson(res);
    return {
        status: res.status,
        data: data as (SubscriptionSummaryResponse & { success?: boolean }) | null,
    };
};

/**
 * @method post
 * @brief 이용권 등록
 */
export const createSubscription = async ({
    centerId,
    memberId,
    body,
}: {
    centerId: string;
    memberId: string;
    body: CreateSubscriptionBody;
}) => {
    const res = await apiClient.post(`v1/centers/${centerId}/members/${memberId}/subscriptions`, {
        json: body,
    });
    const data = await safeJson(res);
    return {
        status: res.status,
        data: data as IFacilitySubscription | null,
    };
};

/**
 * @method put
 * @brief 이용권 수정
 */
export const updateSubscription = async ({
    centerId,
    subscriptionId,
    body,
}: {
    centerId: string;
    subscriptionId: string;
    body: UpdateSubscriptionBody;
}) => {
    const res = await apiClient.put(`v1/centers/${centerId}/subscriptions/${subscriptionId}`, {
        json: body,
    });
    const data = await safeJson(res);
    return {
        status: res.status,
        data: data as IFacilitySubscription | null,
    };
};

/**
 * @method post
 * @brief 이용권 환불
 * @endpoint POST /v1/centers/:centerId/subscriptions/:id/refund
 */
export const refundSubscription = async ({
    centerId,
    subscriptionId,
    body,
}: {
    centerId: string;
    subscriptionId: string;
    body: RefundSubscriptionBody;
}) => {
    const res = await apiClient.post(`v1/centers/${centerId}/subscriptions/${subscriptionId}/refund`, {
        json: body,
    });
    const data = await safeJson(res);
    return {
        status: res.status,
        data: data as IFacilitySubscription | null,
    };
};

/**
 * @method delete
 * @brief 이용권 삭제
 * @endpoint DELETE /v1/centers/:centerId/subscriptions/:id
 */
export const deleteSubscription = async ({
    centerId,
    subscriptionId,
}: {
    centerId: string;
    subscriptionId: string;
}) => {
    const res = await apiClient.delete(`v1/centers/${centerId}/subscriptions/${subscriptionId}`);
    const data = await safeJson(res);
    return {
        status: res.status,
        data,
    };
};

/**
 * @method post
 * @brief 이용권 일시정지 (미래 startedAt이면 예약 정지)
 * @endpoint POST /v1/centers/:centerId/subscriptions/:id/pause
 */
export const pauseSubscription = async ({
    centerId,
    subscriptionId,
    body,
}: {
    centerId: string;
    subscriptionId: string;
    body: PauseSubscriptionBody;
}) => {
    const res = await apiClient.post(`v1/centers/${centerId}/subscriptions/${subscriptionId}/pause`, {
        json: body,
    });
    const data = await safeJson(res);
    return {
        status: res.status,
        data: data as PauseSubscriptionResponse | null,
    };
};

/**
 * @method post
 * @brief 이용권 재활성화
 * @endpoint POST /v1/centers/:centerId/subscriptions/:id/resume
 */
export const resumeSubscription = async ({
    centerId,
    subscriptionId,
    body,
}: {
    centerId: string;
    subscriptionId: string;
    body: ResumeSubscriptionBody;
}) => {
    const res = await apiClient.post(`v1/centers/${centerId}/subscriptions/${subscriptionId}/resume`, {
        json: body,
    });
    const data = await safeJson(res);
    return {
        status: res.status,
        data: data as ResumeSubscriptionResponse | null,
    };
};

/**
 * @method post
 * @brief 정지 취소 (연장 없이 무효화, 이력 보존)
 * @endpoint POST /v1/centers/:centerId/subscriptions/:id/pause-periods/:periodId/cancel
 */
export const cancelPause = async ({
    centerId,
    subscriptionId,
    periodId,
    body,
}: {
    centerId: string;
    subscriptionId: string;
    periodId: string;
    body?: CancelPauseBody;
}) => {
    const res = await apiClient.post(
        `v1/centers/${centerId}/subscriptions/${subscriptionId}/pause-periods/${periodId}/cancel`,
        { json: body ?? {} },
    );
    const data = await safeJson(res);
    return {
        status: res.status,
        data: data as PauseMutationResponse | null,
    };
};

/**
 * @method patch
 * @brief 정지 이력 수정
 * @endpoint PATCH /v1/centers/:centerId/subscriptions/:id/pause-periods/:periodId
 */
export const updatePausePeriod = async ({
    centerId,
    subscriptionId,
    periodId,
    body,
}: {
    centerId: string;
    subscriptionId: string;
    periodId: string;
    body: UpdatePausePeriodBody;
}) => {
    const res = await apiClient.patch(
        `v1/centers/${centerId}/subscriptions/${subscriptionId}/pause-periods/${periodId}`,
        { json: body },
    );
    const data = await safeJson(res);
    return {
        status: res.status,
        data: data as (PauseMutationResponse & { adjustment?: unknown }) | null,
    };
};

/**
 * @method get
 * @brief 정지 이력 조회
 * @endpoint GET /v1/centers/:centerId/subscriptions/:id/pause-periods
 */
export const getPausePeriods = async ({
    centerId,
    subscriptionId,
}: {
    centerId: string;
    subscriptionId: string;
}) => {
    const res = await apiClient.get(
        `v1/centers/${centerId}/subscriptions/${subscriptionId}/pause-periods`,
    );
    const data = await safeJson(res);
    return {
        status: res.status,
        data: data as PausePeriodsResponse | null,
    };
};

/**
 * @method get
 * @brief 이용권 양도 미리보기 — 막혀도 예외 대신 blockedReason 으로 알려 준다.
 * @endpoint GET /v1/centers/:centerId/subscriptions/:id/transfer-preview
 * 구서버에는 이 경로가 없어 404 가 난다(호출부에서 isFacilityTransferUnsupported 로 가른다).
 */
export const getFacilityTransferPreview = async ({
    centerId,
    subscriptionId,
    params,
}: {
    centerId: string;
    subscriptionId: string;
    params: FacilityTransferPreviewParams;
}) => {
    const searchParams: Record<string, string> = {};
    if (params.toMemberId) searchParams.toMemberId = params.toMemberId;
    if (params.days !== undefined) searchParams.days = String(params.days);
    if (params.startDate) searchParams.startDate = params.startDate;
    const res = await apiClient.get(`v1/centers/${centerId}/subscriptions/${subscriptionId}/transfer-preview`, {
        searchParams,
    });
    const data = await safeJson(res);
    return data as (FacilityTransferPreview & { success?: boolean }) | null;
};

/**
 * @method post
 * @brief 이용권 양도 실행. 201 새로 처리 / 200 같은 키 재전송(첫 결과).
 * @endpoint POST /v1/centers/:centerId/subscriptions/:id/transfer
 * 같은 키로 다시 보내면 서버가 첫 결과를 돌려주므로(중복 양도 없음), 토큰 갱신 후 재전송은 기본 동작 그대로 둔다.
 */
export const transferFacilitySubscription = async ({
    centerId,
    subscriptionId,
    body,
}: {
    centerId: string;
    subscriptionId: string;
    body: FacilityTransferBody;
}) => {
    const res = await apiClient.post(`v1/centers/${centerId}/subscriptions/${subscriptionId}/transfer`, {
        json: body,
    });
    const data = await safeJson(res);
    return data as FacilityTransferResult | null;
};

/**
 * @method get
 * @brief 이용권 양도 이력(회원 기준: 넘긴 쪽·받은 쪽 모두, 이용권 기준: 원본·사본 모두)
 * @endpoint GET /v1/centers/:centerId/subscription-transfers
 */
export const getFacilityTransfers = async ({
    centerId,
    memberId,
    subscriptionId,
    page,
    limit,
}: {
    centerId: string;
    memberId?: string;
    subscriptionId?: string;
    page?: number;
    limit?: number;
}) => {
    const searchParams: Record<string, string> = {};
    if (memberId) searchParams.memberId = memberId;
    if (subscriptionId) searchParams.subscriptionId = subscriptionId;
    if (page !== undefined) searchParams.page = String(page);
    if (limit !== undefined) searchParams.limit = String(limit);
    const res = await apiClient.get(`v1/centers/${centerId}/subscription-transfers`, { searchParams });
    const data = await safeJson(res);
    return data as FacilityTransferListResponse | null;
};
